'use client';

import { useMemo } from 'react';

import { SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { useTranslations } from '@ravago/shared/translations/client';

import { useShoppingCartContext } from '../../../../../context/shopping-cart-context';
import { useCombinedPricingState } from '../../../../../hooks/use-combined-pricing-state';
import type { CombinedSuggestion } from '../../../../../utils/quote-pricing/combined-pricing.utils';

import { QuantitySelector } from '../../../../shared/quote-pricing/quantity-selector';
import { SmallLotActiveTierSummary } from './small-lot-active-tier-summary';
import { SmallLotQuantityBreaks } from './small-lot-quantity-breaks';
import { hasSmallLotMtsLocalLeadTime } from '@ravago/shared/page-elements-radiance/utils/lead-time';

interface Props {
   pricing: SmallLotPricing;
   productName: string;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   smallLotDefaultAddress?: Address;
   onRequestQuote: () => void;
}

export const SmallLotPricingContent = ({
   pricing,
   productName,
   leadTimeIndicators,
   leadTimeEnabled,
   smallLotDefaultAddress,
   onRequestQuote
}: Props) => {
   const translate = useTranslations();
   const { addToCart, isAddingToCart } = useShoppingCartContext();

   const { inputValue, hasSettled, resolution, onInputChange, selectQuantity } = useCombinedPricingState({ smallLotPricing: pricing });

   const isValid = resolution.kind === 'small-lot';
   const selectedQuantity = isValid ? resolution.quantity : 0;

   const hasMtsLocal = useMemo(() => {
      if (!leadTimeEnabled || !smallLotDefaultAddress) return undefined;

      return hasSmallLotMtsLocalLeadTime(leadTimeIndicators, pricing, smallLotDefaultAddress);
   }, [leadTimeEnabled, leadTimeIndicators, pricing, smallLotDefaultAddress]);

   const suggestions = useMemo<CombinedSuggestion[]>(() => {
      if (!hasSettled || resolution.kind !== 'suggestion') return [];
      return [resolution.lower, resolution.higher].filter((s): s is CombinedSuggestion => s !== undefined);
   }, [hasSettled, resolution]);

   const handleAddToCart = () => {
      if (resolution.kind !== 'small-lot') return;
      addToCart(
         { type: 'SMALL_LOT', id: pricing.ohmProductId.toString() },
         { id: pricing.ohmProductId, description: productName },
         pricing.packaging.name,
         { amount: resolution.quantity, unitOfMeasure: pricing.packaging.unitOfMeasure }
      );
   };

   return (
      <>
         <SmallLotQuantityBreaks
            tiers={pricing.tiers}
            packaging={pricing.packaging}
            selectedQuantity={selectedQuantity}
            isQuantityValid={isValid}
            hasMtsLocal={hasMtsLocal}
            onRequestQuote={onRequestQuote}
         />

         <QuantitySelector
            value={inputValue}
            suffix={pricing.packaging.unitOfMeasure}
            error={hasSettled && resolution.kind === 'suggestion'}
            suggestions={suggestions}
            smallLotPackaging={pricing.packaging}
            onChange={onInputChange}
            onSelect={selectQuantity}
            id="small-lot-quantity-input"
         />

         {resolution.kind === 'small-lot' && (
            <SmallLotActiveTierSummary
               tier={resolution.tier}
               selectedQuantity={resolution.quantity}
               packaging={pricing.packaging}
               hasMtsLocal={hasMtsLocal}
            />
         )}

         <Divider />

         <Button fullWidth disabled={!isValid || isAddingToCart} onClick={handleAddToCart}>
            {translate('dynamic-product-block.small-lot.add-to-cart')}
         </Button>
      </>
   );
};
